import React, { useState, useEffect } from 'react';
import { api } from '../api';

// Barra de distribución de qty por etapa (estilo stacked bar)
function DistBar({ total, disponible, solicitada, enDespacho, entregada, unidad }) {
  if (total <= 0) return null;
  const pct = (v) => Math.round((v / total) * 100);
  const segments = [
    { value: disponible,  color: 'var(--green)',   label: 'En bodega' },
    { value: solicitada,  color: 'var(--amber)',   label: 'Solicitado' },
    { value: enDespacho,  color: 'var(--blue)',    label: 'En despacho' },
    { value: entregada,   color: '#6b7280',        label: 'Entregado' },
  ].filter(s => s.value > 0);

  return (
    <div>
      <div style={{ display: 'flex', height: 8, borderRadius: 6, overflow: 'hidden', gap: 1, marginBottom: 5 }}>
        {segments.map((s, i) => (
          <div key={i} style={{ width: pct(s.value) + '%', background: s.color, minWidth: s.value > 0 ? 3 : 0 }} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {segments.map((s, i) => (
          <span key={i} style={{ fontSize: 10.5, color: s.color, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 7, height: 7, borderRadius: 2, background: s.color, display: 'inline-block' }} />
            {s.value} {unidad} {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// Flecha conectora
function Arrow({ active }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <svg width="36" height="20" viewBox="0 0 36 20">
        <line x1="2" y1="10" x2="28" y2="10"
          stroke={active ? 'var(--primary)' : '#d1d5db'}
          strokeWidth={active ? 2.5 : 1.5}
          strokeDasharray={active ? 'none' : '4 3'}
        />
        <polyline points="22,4 31,10 22,16"
          fill="none" stroke={active ? 'var(--primary)' : '#d1d5db'}
          strokeWidth={active ? 2.5 : 1.5} strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

export default function Pipeline() {
  const [lotes, setLotes] = useState([]);
  const [lotSearch, setLotSearch] = useState('');
  const [lotEventos, setLotEventos] = useState([]);
  const [lotSearched, setLotSearched] = useState('');
  const [activeStage, setActiveStage] = useState(null);

  useEffect(() => { api.getLotes().then(setLotes).catch(console.error); }, []);

  // Lotes en cada etapa (un lote puede estar en varias simultáneamente)
  const enBodega    = lotes.filter(l => l.qtyDisponible > 0);
  const solicitados = lotes.filter(l => l.qtySolicitada > 0);
  const enDespacho  = lotes.filter(l => l.qtyEnDespacho > 0);
  const entregados  = lotes.filter(l => l.qtyEntregada > 0);
  const rechazados  = lotes.filter(l => l.condicion === 'rej');

  const STAGES = [
    {
      id: 'bodega',
      icon: '🏪', label: 'En bodega',
      color: 'var(--green)', bg: 'var(--green-bg)', border: 'var(--green-border)',
      items: enBodega,
      qtyKey: 'qtyDisponible',
      desc: 'Disponible para solicitar',
    },
    {
      id: 'solicitado',
      icon: '📋', label: 'Solicitado',
      color: 'var(--amber)', bg: 'var(--amber-bg)', border: 'var(--amber-border)',
      items: solicitados,
      qtyKey: 'qtySolicitada',
      desc: 'Pedido pendiente de aprobar',
    },
    {
      id: 'despacho',
      icon: '⚙', label: 'En despacho',
      color: 'var(--blue)', bg: 'var(--blue-bg)', border: 'var(--blue-border)',
      items: enDespacho,
      qtyKey: 'qtyEnDespacho',
      desc: 'Bodega preparando envío',
    },
    {
      id: 'entregado',
      icon: '✓', label: 'Entregado',
      color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb',
      items: entregados,
      qtyKey: 'qtyEntregada',
      desc: 'Confirmado por chef',
    },
  ];

  const activeStageData = STAGES.find(s => s.id === activeStage);

  const buscarLote = async () => {
    if (!lotSearch.trim()) return;
    const data = await api.getEventos(lotSearch.trim());
    setLotEventos(data);
    setLotSearched(lotSearch.trim());
  };

  const LOT_COLORS = {
    'Recepción':       { dot: 'var(--primary)', bg: 'var(--primary-light)', border: 'var(--primary-mid)' },
    'Despacho cocina': { dot: 'var(--blue)',    bg: 'var(--blue-bg)',    border: 'var(--blue-border)' },
    'Pedido cocina':   { dot: 'var(--amber)',   bg: 'var(--amber-bg)',   border: 'var(--amber-border)' },
    'Recepción cocina':{ dot: '#16a34a',        bg: 'var(--green-bg)',   border: 'var(--green-border)' },
    'Sync SAP':        { dot: 'var(--text3)',   bg: 'var(--bg)',         border: 'var(--border)' },
    'Alerta vencimiento': { dot: 'var(--amber)', bg: 'var(--amber-bg)', border: 'var(--amber-border)' },
  };

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Pipeline de lotes</div>
          <div className="topbar-meta">Flujo visual de materia prima — dónde están las cantidades de cada lote</div>
        </div>
      </div>
      <div style={{ background: 'var(--blue-bg)', borderBottom: '1px solid var(--blue-border)', padding: '9px 28px', fontSize: 12.5, color: 'var(--blue)' }}>
        <strong>🔀 Cómo leer este módulo:</strong> Un lote puede estar en varias etapas simultáneamente porque se despachan <em>cantidades parciales</em>. La barra de colores muestra cómo se distribuye la cantidad total del lote entre bodega, despacho y cocina.
      </div>

      <div className="page-content">

        {/* ── Diagrama de etapas ── */}
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-title"><div className="ct-icon">⟶</div> Flujo de materia prima · por lote</div>

          <div style={{ display: 'flex', alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 4 }}>
            {/* Nodo recepción */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80, flexShrink: 0, paddingTop: 10 }}>
              <div style={{ width: 52, height: 52, borderRadius: 13, background: 'var(--primary-light)', border: '2px solid var(--primary-mid)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📥</div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--primary)', marginTop: 5, textAlign: 'center' }}>Recepción</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--primary)' }}>{lotes.length}</div>
            </div>

            {STAGES.map((stage) => (
              <React.Fragment key={stage.id}>
                <Arrow active={stage.items.length > 0} />
                <div
                  onClick={() => setActiveStage(activeStage === stage.id ? null : stage.id)}
                  style={{
                    minWidth: 140, flexShrink: 0, cursor: 'pointer',
                    border: `2px solid ${activeStage === stage.id ? stage.color : stage.border}`,
                    borderRadius: 14, padding: '14px 14px 12px', textAlign: 'center',
                    background: activeStage === stage.id ? stage.bg : 'var(--surface)',
                    transition: 'all 0.18s',
                    boxShadow: activeStage === stage.id ? `0 0 0 4px ${stage.bg}` : 'var(--shadow-sm)',
                  }}
                >
                  <div style={{ fontSize: 24, marginBottom: 5 }}>{stage.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 800, lineHeight: 1.25, marginBottom: 5 }}>{stage.label}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: stage.items.length > 0 ? stage.color : 'var(--text3)', lineHeight: 1, marginBottom: 3 }}>
                    {stage.items.length}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text3)', fontWeight: 600 }}>{stage.desc}</div>
                  {stage.items.length > 0 && (
                    <div style={{ fontSize: 10.5, color: stage.color, fontWeight: 700, marginTop: 5 }}>
                      {stage.items.reduce((s, l) => s + parseFloat(l[stage.qtyKey] || 0), 0).toFixed(0)} {stage.items[0]?.unidad || 'kg'} total
                    </div>
                  )}
                </div>
              </React.Fragment>
            ))}

            <Arrow active={entregados.length > 0} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 70, flexShrink: 0, paddingTop: 10 }}>
              <div style={{ width: 52, height: 52, borderRadius: 13, background: 'var(--green-bg)', border: '2px solid var(--green-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🍳</div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--green)', marginTop: 5 }}>Cocina</div>
            </div>
          </div>

          {rechazados.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Desvíos</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--red-bg)', border: '1.5px solid var(--red-border)', borderRadius: 9, padding: '7px 13px' }}>
                <span>✗</span>
                <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--red)' }}>{rechazados.length} lote{rechazados.length !== 1 ? 's' : ''} rechazado{rechazados.length !== 1 ? 's' : ''}</div>
              </div>
            </div>
          )}
        </div>

        {/* ── Panel de etapa seleccionada ── */}
        {activeStageData && (
          <div className="card" style={{ marginBottom: 18, border: `2px solid ${activeStageData.border}` }}>
            <div className="section-header">
              <div className="section-title">
                {activeStageData.icon} {activeStageData.label}
                <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600, marginLeft: 8 }}>
                  {activeStageData.items.length} lote{activeStageData.items.length !== 1 ? 's' : ''}
                </span>
              </div>
              <button className="btn btn-ghost btn-xs" onClick={() => setActiveStage(null)}>✕</button>
            </div>
            {activeStageData.items.length === 0
              ? <div className="empty-state"><div className="es-icon">{activeStageData.icon}</div><div className="es-text">Sin lotes en esta etapa</div></div>
              : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                  {activeStageData.items.map(l => (
                    <div key={l.lote} style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 11, padding: '13px 15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <span className="lot-tag">{l.lote}</span>
                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                          {l[activeStageData.qtyKey]} {l.unidad} aquí
                        </span>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{l.producto}</div>
                      <DistBar
                        total={l.qtyTotal} disponible={l.qtyDisponible}
                        solicitada={l.qtySolicitada} enDespacho={l.qtyEnDespacho}
                        entregada={l.qtyEntregada} unidad={l.unidad}
                      />
                    </div>
                  ))}
                </div>
              )
            }
          </div>
        )}

        {/* ── Todos los lotes activos con su distribución ── */}
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-title"><div className="ct-icon">📦</div> Distribución de cantidades por lote</div>
          {lotes.length === 0
            ? <div className="empty-state"><div className="es-icon">📦</div><div className="es-text">Sin lotes activos</div></div>
            : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Lote</th>
                      <th>Producto</th>
                      <th>Total</th>
                      <th>Disponible</th>
                      <th>Solicitado</th>
                      <th>En despacho</th>
                      <th>Entregado</th>
                      <th>Distribución</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lotes.map(l => (
                      <tr key={l.lote}>
                        <td><span className="lot-tag">{l.lote}</span></td>
                        <td><strong>{l.producto}</strong></td>
                        <td style={{ fontSize: 12, color: 'var(--text2)' }}>{l.qtyTotal} {l.unidad}</td>
                        <td>
                          <span style={{ fontWeight: 700, color: l.qtyDisponible > 0 ? 'var(--green)' : 'var(--text3)' }}>
                            {l.qtyDisponible} {l.unidad}
                          </span>
                        </td>
                        <td style={{ fontSize: 12, color: l.qtySolicitada > 0 ? 'var(--amber)' : 'var(--text3)' }}>
                          {l.qtySolicitada > 0 ? `${l.qtySolicitada} ${l.unidad}` : '—'}
                        </td>
                        <td style={{ fontSize: 12, color: l.qtyEnDespacho > 0 ? 'var(--blue)' : 'var(--text3)' }}>
                          {l.qtyEnDespacho > 0 ? `${l.qtyEnDespacho} ${l.unidad}` : '—'}
                        </td>
                        <td style={{ fontSize: 12, color: l.qtyEntregada > 0 ? '#6b7280' : 'var(--text3)' }}>
                          {l.qtyEntregada > 0 ? `${l.qtyEntregada} ${l.unidad}` : '—'}
                        </td>
                        <td style={{ minWidth: 140 }}>
                          <DistBar
                            total={l.qtyTotal} disponible={l.qtyDisponible}
                            solicitada={l.qtySolicitada} enDespacho={l.qtyEnDespacho}
                            entregada={l.qtyEntregada} unidad={l.unidad}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>

        {/* ── Rastreador de lote ── */}
        <div className="card">
          <div className="card-title"><div className="ct-icon">🔍</div> Rastrear lote en el flujo</div>
          <div style={{ display: 'flex', gap: 10, marginBottom: lotEventos.length ? 20 : 0 }}>
            <input
              value={lotSearch}
              onChange={e => setLotSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && buscarLote()}
              placeholder="Código de lote (Ej: TOM-001-A)..."
              style={{ flex: 1 }}
            />
            <button className="btn btn-primary" onClick={buscarLote}>Rastrear</button>
            {lotEventos.length > 0 && (
              <button className="btn btn-ghost" onClick={() => { setLotEventos([]); setLotSearched(''); setLotSearch(''); }}>
                Limpiar
              </button>
            )}
          </div>

          {lotEventos.length > 0 && (
            <>
              {/* Etapas recorridas */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11.5, color: 'var(--text3)', fontWeight: 700 }}>Trazabilidad del lote</span>
                <span className="lot-tag">{lotSearched}</span>
                <span style={{ color: 'var(--border2)' }}>·</span>
                {lotEventos.map((e, i) => {
                  const c = LOT_COLORS[e.evento];
                  return c ? (
                    <React.Fragment key={i}>
                      {i > 0 && <span style={{ color: 'var(--border2)', fontSize: 11 }}>→</span>}
                      <span style={{ fontSize: 11, fontWeight: 700, color: c.dot, background: c.bg, border: `1px solid ${c.border}`, borderRadius: 20, padding: '2px 8px' }}>
                        {e.evento}
                      </span>
                    </React.Fragment>
                  ) : null;
                })}
              </div>

              {/* Timeline */}
              <div style={{ position: 'relative', paddingLeft: 32 }}>
                <div style={{ position: 'absolute', left: 11, top: 8, bottom: 8, width: 2, background: 'var(--border)', borderRadius: 2 }} />
                {lotEventos.map((e, i) => {
                  const c = LOT_COLORS[e.evento] || { dot: 'var(--text3)', bg: 'var(--bg)', border: 'var(--border)' };
                  return (
                    <div key={e.id || i} style={{ position: 'relative', marginBottom: i < lotEventos.length - 1 ? 10 : 0 }}>
                      <div style={{ position: 'absolute', left: -21, top: 11, width: 13, height: 13, borderRadius: '50%', background: c.bg, border: `2.5px solid ${c.dot}`, zIndex: 1 }} />
                      <div style={{ background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: 10, padding: '10px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 800, color: c.dot, marginBottom: 2 }}>{e.evento}</div>
                          <div style={{ fontSize: 12, color: 'var(--text2)' }}>{e.detalle}</div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)' }}>{e.ts}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{e.responsable}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {lotEventos.length === 0 && lotSearched && (
            <div className="empty-state">
              <div className="es-icon">🔍</div>
              <div className="es-text">No se encontraron eventos para "{lotSearched}"</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
